import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { buildQueryFromState, capitalizeFirstLetter, extractItemsFromStore, getCurrentLanguage } from '../../helpers'
import PropTypes from 'prop-types'
import { CalendarField, CustomField } from '../CustomField'
import Alert from '../Alert'
import Loader from '../Loader'
import SearchFilterApartment from '../SearchFilterApartment'
import Button from '../Button'
import DateRangePicker from '../DateRangePicker'
import DestinationsApartmentList from '../DestinationsApartmentList'
import { fetchApartmentPossibleParams, fetchPossibleParams } from '../../api/fetchPossibleParams'
import {
    setPossibleDestinations,
    setPossibleDepartures,
    setPossibleTransports,
    setPopularDestinations
} from '../../actions/dataActions'
import {
    resetSelection,
    setAdults,
    setTransport,
    resetParticipants,
    setFormField
} from '../../actions/formActions'
import destinationIcon from '@assets/img/search-engine/destination-icon.svg';
import calendarIcon from '@assets/img/search-engine/calendar-icon.svg';
import participantIcon from '@assets/img/search-engine/participants-icon.svg';
import "./style.scss"
import Participants from '../Participants'
import { t } from 'i18next'
import { DEFAULT_ADULT_COUNT, DEFAULT_CHILDREN_COUNT } from '../../../search-engine/constants'
import { PAGE_SEARCH } from '../../consts/endpoints'

const TripSearchApartmentForm = ({
    customDestination,
    customTransport,
    facilities,
    feeding,
    operator,
    standard,
    minDays,
    maxDays,
    priceMin,
    priceMax,
    priceFor,
    form,
    data,
    setPopularDestinations,
    setPossibleDestinations,
    setPossibleDepartures,
    setPossibleTransports,
    setTransport,
    setAdults,
    resetSelection,
    resetParticipants,
    setFormField
}) => {
    const { t, i18n } = useTranslation()
    const [isLoading, setIsLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState(null)
    const [destinationPlaceholder, setDestinationPlaceholder] = useState(null)
    const [departurePlaceholder, setDeparturePlaceholder] = useState(null)
    const [participantsPlaceholder, setParticipantsPlaceholder] = useState(null)
    const [destinationResetKey, setDestinationResetKey] = useState(0)
    const [isValidParticipants, setIsValidParticipants] = useState(true)
    const [urlParamsApplied, setUrlParamsApplied] = useState(false)

    useEffect(() => {
        const currentLang = getCurrentLanguage()

        i18n.changeLanguage(currentLang).then(r => null)
    }, [i18n])

    useEffect(() => {
        if (!isLoading && !urlParamsApplied) {
            const params = new URLSearchParams(window.location.search);
            const entries = Array.from(params.entries());

            entries.forEach(([key, value]) => {
                if (key in form) {
                    setFormField(key, value);
                }
            })

            setUrlParamsApplied(true);
        }
    }, [isLoading, urlParamsApplied])

    useEffect(() => {
        fetchApartmentPossibleParams(customTransport)
            .then((data) => {
                //setPopularDestinations(data.popular)
                setPossibleDestinations(data.countryRegion)
                setAdults(data.defaultValues.adults)
            })
            .catch((err) => setErrorMessage(err.message))
            .finally(() => {
                setIsLoading(false)
            });
    }, [])

    useEffect(() => {
        if (customDestination) {
            const destinationValue = capitalizeFirstLetter(customDestination);
            
            // Sprawdź czy jest parametr typ[]=objazdowa w URL
            const params = new URLSearchParams(window.location.search);
            const typValues = params.getAll('typ[]');
            const hasObjazdowa = typValues.includes('objazdowa');
            
            if (hasObjazdowa) {
                // Jeśli jest objazdowa, zamień kraj na "Kraj.Wyc. Objazdowe"
                const objazdowaValue = `${destinationValue}.Wyc. Objazdowe`;
                setFormField('kierunek', objazdowaValue);
            } else {
                setFormField('kierunek', destinationValue);
            }
        }
    }, [])

    useEffect(() => {
        const defaultPlaceholder = t('form.destinations.field_placeholder');
        const params = new URLSearchParams(window.location.search);
        
        // Sprawdź parametry URL
        const typValues = params.getAll('typ[]');
        const hasObjazdowa = typValues.includes('objazdowa');
        const cityValues = params.getAll('city[]');
        const hasCity = cityValues.length > 0;
        
        let destinationsLabel = defaultPlaceholder;
        
        if (form.kierunek && form.kierunek !== '') {
            if (hasObjazdowa) {
                // Jeśli jest typ[]=objazdowa, wyświetl tylko kraj (pierwsza część)
                const kierunekParts = form.kierunek.split(';');
                const firstPart = kierunekParts[0];
                const parts = firstPart.split('.');
                destinationsLabel = parts[0]; // Tylko kraj
            } else if (hasCity) {
                // Jeśli jest city[], wyświetl tylko miasto
                destinationsLabel = cityValues.join(', ');
            } else {
                // Standardowa logika - użyj extractItemsFromStore
                destinationsLabel = extractItemsFromStore(form.kierunek, defaultPlaceholder);
            }
        }

        let label = destinationsLabel;

        if (form.hotel && form.hotel !== '') {
            label = destinationsLabel === defaultPlaceholder ? form.hotel : `${destinationsLabel}, ${form.hotel}`;
        }

        setDestinationPlaceholder(label);
    }, [form.kierunek, form.hotel])

    useEffect(() => {
        const defaultPlaceholder = t('form.participants.default_placeholder');
        const { dorosli, dzieci } = form;
        let placeholder = null;

        if (dorosli === DEFAULT_ADULT_COUNT && dzieci === DEFAULT_CHILDREN_COUNT.length) {
            placeholder = defaultPlaceholder
        } else {
            placeholder = dzieci.length > 0 ?
                `${dorosli} + ${dzieci.length}` :
                `${dorosli} ${t('form.participants.adult')}`;
        }

        setParticipantsPlaceholder(placeholder)
    }, [form.dorosli, form.dzieci])

    if (isLoading) {
        return <Loader />
    }

    if (errorMessage !== null) {
        return <Alert variant="danger" header={t('common.error_header')} message={errorMessage} />
    }

    const submitHandler = (e) => {
        e.preventDefault();
        const params = new URLSearchParams(window.location.search);
        let { kierunek, ...filterForm } = form;

        let countryName = null;
        
        if (kierunek) {
            const kierunekValues = kierunek.split(';');
            const cities = [];
            const processedKierunekValues = [];
            
            kierunekValues.forEach(val => {
                const lowerVal = val.toLowerCase();
                const parts = val.split('.');
                
                if (parts.length === 3) {
                    // Standardowy przypadek z 3 poziomami (kraj.region.miasto)
                    cities.push(parts[2]);
                    const regionKey = parts[0] + '.' + parts[1];
                    if (!processedKierunekValues.includes(regionKey)) {
                        processedKierunekValues.push(regionKey);
                    }
                } else {
                    // Standardowy przypadek - dodaj wartość jak jest
                    processedKierunekValues.push(val);
                }
            });
            
            kierunek = processedKierunekValues.join(';');
            
            if (cities.length > 0) {
                cities.forEach(city => {
                    params.append('city[]', city);
                });
            }
        }

        const filters = {
            facilities,
            feeding,
            operator,
            standard,
            minDays,
            maxDays,
            priceMin,
            priceMax,
            priceFor
        };
        
        const arrayParams = [
            { key: 'facilities', param: 'udogodnienia[]' },
            { key: 'feeding', param: 'wyzywienie[]' },
            { key: 'operator', param: 'operator[]' }
        ];
        
        const valueParams = [
            { key: 'standard', param: 'standard' },
            { key: 'minDays', param: 'min-dni' },
            { key: 'maxDays', param: 'max-dni' },
            { key: 'priceMin', param: 'cena-min' },
            { key: 'priceMax', param: 'cena-max' },
            { key: 'priceFor', param: 'cena-laczna' }
        ];
        
        arrayParams.forEach(({ key, param }) => {
            if (filters[key]) {
                JSON.parse(filters[key]).forEach(el => {
                    params.set(key, el);
                });
            }
        });
        
        valueParams.forEach(({ key, param }) => {
            if (filters[key]) {
                params.set(key, JSON.parse(filters[key]));
            }
        });

        Object.entries(filterForm)
            .filter(([, value]) => Array.isArray(value) ? value.length > 0 : value)
            .map(([key, value]) => params.set(key, value));

        if (kierunek) {
            window.location.href = `search-v2/${kierunek}?${params.toString()}`
        } else {
            window.location.href = `search-v2/?${params.toString()}`
        }

        return false;
    }

    const resetDepartures = () => {
        resetSelection('z')
    }

    const resetDestinations = () => {
        resetSelection('kierunek')
        setFormField('hotel', '')
        setDestinationResetKey(prev => prev + 1)
    }

    const resetParticipantsHandler = () => {
        resetParticipants()
    }

    return (
        <form onSubmit={submitHandler} className="trip-apartment-form">
            <div>
                <CustomField
                    icon={destinationIcon}
                    title={t('form.destinations.field_label')}
                    placeholder={destinationPlaceholder}
                    modalId="destinations"
                    extraCssClass="trip-field--destinations box-shadow"
                    onReset={resetDestinations}
                >
                    <DestinationsApartmentList
                        popular={data.popularDestinations}
                        destinations={data.possibleDestinations}
                        resetTrigger={destinationResetKey}
                    />
                </CustomField>
                <CalendarField
                    icon={calendarIcon}
                    extraCssClass="trip-field--calendar box-shadow"
                >
                    <DateRangePicker
                        startLabel={t(`form.dates.start_field_label`)}
                        endLabel={t(`form.dates.end_field_label`)}
                        endPlaceholder={t(`form.dates.end_field_placeholder`)}
                    />
                </CalendarField>

                <CustomField
                    icon={participantIcon}
                    title={t('form.participants.field_label')}
                    placeholder={participantsPlaceholder}
                    modalId="participants"
                    extraCssClass="trip-field--participants box-shadow"
                    onReset={resetParticipantsHandler}
                    isValid={isValidParticipants}
                >
                    <Participants onValidityChange={setIsValidParticipants}/>
                </CustomField>

                <Button
                    label={t('common.btn_search')}
                    variant="primary"
                    type="submit"
                    extraCss="trip-btn--search box-shadow animation-bg-color"
                />
            </div>
        </form>
    )
};

TripSearchApartmentForm.propTypes = {
    customDestination: PropTypes.string,
    form: PropTypes.object,
    data: PropTypes.object,
    setPopularDestinations: PropTypes.func,
    setPossibleDestinations: PropTypes.func,
    setPossibleDepartures: PropTypes.func,
    setPossibleTransports: PropTypes.func,
    setAdults: PropTypes.func,
    setTransport: PropTypes.func,
    resetSelection: PropTypes.func,
    setFormField: PropTypes.func,
};

const mapStateToProps = (state) => ({
    form: state.formReducer,
    data: state.dataReducer
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setPopularDestinations,
            setPossibleDestinations,
            setPossibleDepartures,
            setPossibleTransports,
            setAdults,
            setTransport,
            resetSelection,
            resetParticipants,
            setFormField
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(TripSearchApartmentForm)
